import { Directive, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DestroyController } from '@celerofinancas/class-utils';
import { ChatService } from '@celerofinancas/service-utils';
import { CompanyState } from '@celerofinancas/state-management';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';
import { environment } from '@environments/environment';
import { Store } from '@ngxs/store';
import { APPLICATION_THEME_DATA_ATTRIBUTE, APPLICATION_THEMES } from '@shared/constants/theme';
import { AuthService } from '@shared/services/auth.service';
import { CheckFirstAccessService } from '@shared/services/check-first-access/check-first-access.service';
import { CsatSurveyService } from '@shared/services/csat-survey/csat-survey.service';
import { filter, takeUntil } from 'rxjs';

/**
 * Directive for exchanging tokens
 */
@Directive()
export class BootstrapCoordinator extends DestroyController implements OnInit {
  /**
   * Check First Access Service
   */
  private readonly firstAccessService = inject(CheckFirstAccessService);

  /**
   * Chat service
   */
  private readonly chat = inject(ChatService);

  /**
   * CSAT survey service
   */
  private readonly csatSurvey = inject(CsatSurveyService);

  /**
   * Auth service
   */
  private readonly auth = inject(AuthService);

  /**
   * Token handler service
   */
  private readonly tokens = inject(TokenHandlerService);

  /**
   * Store management
   */
  protected readonly store = inject(Store);

  /**
   * Current route
   */
  private readonly route = inject(ActivatedRoute);

  /**
   * Lifecycle hook that is called after the component has been initialized.
   */
  public ngOnInit(): void {
    this.firstAccessService.verifyAcceptTermsOrComplete();
    this.handleChatInitialization();
  }

  /**
   * Handles the chat initialization after user is loaded
   */
  private handleChatInitialization(): void {
    this.store
      .select(CompanyState.getActiveCompanyPk)
      .pipe(
        filter((companyPk) => !!companyPk),
        takeUntil(this.destroyController$),
      )
      .subscribe(() => {
        if (environment.chat.enabled) {
          const theme = document.body.getAttribute(APPLICATION_THEME_DATA_ATTRIBUTE);
          const replaces =
            theme === APPLICATION_THEMES.CAIXA_GOV
              ? { configs: { action_color: '#597771', background_color: '#829E99' } }
              : undefined;
          this.chat.initialize(replaces);
          this.csatSurvey.triggerForCurrentRoute();
        }
      });
  }
}
